/** Building the journal's two halves out of the live game.
 *
 * The shell may not call a rule module (I10) and the observation does not carry
 * an object's instance payload, so what a Oracle's Hut is asking for and whether
 * the selected hero could pay it are both questions only the host can ask. It
 * asks them here and hands the screen finished lines.
 *
 * The chronicle is the opposite way round: `chronicle` in `@game/ui` is pure
 * and takes the event log, so all this side supplies is the three name
 * lookups, which need state - and the answer to "whose news is this?", which
 * needs state too. `eventAudience` below is that answer, and it guards the
 * journal, the build toast and the sound cues alike.
 */

import type {
  ContentRegistry, GameState, HeroId, MapObjectState, ObjectId, PlayerId,
} from "@game/core";
import { entityKeys, makeCtx, payloadOf, questDone, questKind, questSatisfied, questText } from "@game/core";
import type { ChronicleLine, JournalNames, JournalQuest } from "@game/ui";
import { chronicle } from "@game/ui";
import type { GameEvent } from "@game/core";

import { objectDisplayNameOf } from "./names.js";

/**
 * Every quest this player has seen and not yet finished, plus the ones he has.
 *
 * "Seen" is the fog test and it is the only place this function could leak: a
 * journal that listed every Oracle's Hut on the map would be a scouting report.
 * `fog.seen` is the same plane the observation masks with, so the two agree by
 * construction.
 */
export function journalQuests(
  state: GameState, content: ContentRegistry, viewer: PlayerId, hero: HeroId | null,
): readonly JournalQuest[] {
  const c = makeCtx(state, content);
  const fog = state.fog[viewer] ?? null;
  const h = hero === null ? null : state.heroes[hero] ?? null;
  const out: JournalQuest[] = [];

  for (const id of entityKeys(state.objects)) {
    const o: MapObjectState | undefined = state.objects[id as ObjectId];
    if (o === undefined || o.removed) continue;
    const pay = payloadOf(c, o);
    if (questKind(pay) === "none") continue;
    if (fog !== null) {
      const i = (o.z * state.map.height + o.y) * state.map.width + o.x;
      // 0 is UNSEEN; remembered ground still counts, because a hut you walked
      // past last week is a hut you know about.
      if ((fog.seen[i] ?? 0) === 0) continue;
    }
    out.push({
      id: id as string,
      title: objectDisplayNameOf(o.def, content),
      code: questText(pay),
      x: o.x, y: o.y, z: o.z,
      done: questDone(c, o, viewer),
      satisfied: h === null ? null : questSatisfied(c, h, pay),
    });
  }
  return out;
}

/** The three id-to-name lookups the chronicle needs. */
export function journalNames(state: GameState, content: ContentRegistry): JournalNames {
  return {
    town: (id) => state.towns[id as never]?.name ?? "a town",
    hero: (id) => {
      const h = state.heroes[id as never];
      if (h === undefined) return "A hero";
      return content.heroes.get(h.def)?.name ?? "A hero";
    },
    player: (id) => {
      const p = state.players[id as never];
      // The colour is what the player sees on the map and on the scoreboard;
      // there is no other name for a kingdom in this build.
      const colour = p?.colour ?? "someone";
      return colour.slice(0, 1).toUpperCase() + colour.slice(1);
    },
  };
}

/**
 * Who this event is news to. `null` means everybody.
 *
 * The event log is one shared list: the turn loop hands every player's commands
 * to the same `GameSession.execute`, so the computer's town building and the
 * computer's artifact pickups land in it beside the player's own. Read back
 * unfiltered - which is what the chronicle, the build toast and the sound cues
 * all did - that log is a scouting report. Measured on seed 77 at 48x48, three
 * human turns in: three `BuildingBuilt` events in the log, all three from the
 * computer's capital, none from the player's own town. The player was being
 * told the enemy's build order, by name, and hearing it.
 *
 * An audience and not an owner, because two of these events have two sides. A
 * town changing hands is news to the kingdom that took it AND to the kingdom
 * that lost it - filtering on the taker alone would have hidden from a player
 * that his own capital had fallen, which is the one line he must never miss.
 *
 * Ownership is read from the CURRENT state wherever the entity still exists,
 * because that is the only ownership anyone still has: a town captured since
 * carries its old owner's builds into the new owner's chronicle. Two events
 * cannot be answered that way at all - the hero record is deleted before
 * `HeroDefeated` is read, and a lost caravan is by definition one whose
 * destination now belongs to somebody else - so those two carry their owner.
 *
 * Turn structure is deliberately NOT owned. `TurnStarted` names a player but is
 * the chronicle's only clock: fold it away and every line after it is stamped
 * with the wrong day.
 */
export function eventAudience(
  state: GameState, e: GameEvent,
): readonly PlayerId[] | null {
  switch (e.type) {
    // The calendar, the weather and the end of the war: everyone's.
    case "TurnStarted": case "TurnEnded": case "WeekStarted": case "MonthStarted":
    case "GameOver":
      return null;

    case "HeroHired": case "FogRevealed": case "ObjectCaptured":
    case "ResourceGained": case "ResourceSpent":
    case "InteractionOpened": case "InteractionClosed":
    case "HeroDefeated": case "CaravanLost":
      return [e.player];

    case "Message":
      return e.player === null ? null : [e.player];

    case "TownCaptured":
      return e.from === null ? [e.player] : [e.player, e.from];

    case "HeroMoved": case "HeroMovementSpent": case "HeroLeveledUp":
    case "HeroGainedExperience": case "HeroDismissed":
    case "ObjectVisited": case "ArtifactGained": case "ArtifactEquipped":
    case "ArtifactGiven": case "HeroesMet":
    case "SpellLearned": {
      // A hero who is no longer in state - dismissed, or killed later in the
      // same batch - cannot be attributed, and an unattributable line is shown
      // rather than hidden: the cost of the wrong choice here is the player
      // missing his own hero's last find.
      const owner = state.heroes[e.hero]?.owner;
      return owner === undefined ? null : [owner];
    }

    /**
     * A town's owner may be `null` - a neutral town grows creatures every week
     * and nobody is entitled to hear about it - so an unowned town's news has
     * an audience of nobody rather than an audience of everybody.
     */
    case "BuildingBuilt": case "CreaturesRecruited": case "CreaturesGrew":
    case "CreaturesUpgraded": case "GuildSpellsSet": case "SpellResearched":
    case "CaravanSent": case "CaravanArrived": {
      const owner = state.towns[e.town]?.owner ?? null;
      return owner === null ? [] : [owner];
    }

    /**
     * Everything else is unownable from here - a battle event names a side and
     * not a kingdom, a stack event names an army - and unownable means shown.
     * Defaulting the other way would silence the player's own battle rather
     * than the computer's, which is the worse of the two mistakes.
     */
    default:
      return null;
  }
}

/**
 * The events this viewer may be told about, in order.
 *
 * A spectator is told everything. That seat is not a player - it is the
 * reviewer's view of a fixture and the replay of a finished game - and it is
 * the one case where an omniscient chronicle is the point rather than a leak.
 */
export function viewerNews(
  events: readonly GameEvent[], state: GameState, viewer: PlayerId,
): readonly GameEvent[] {
  if ((viewer as string) === "spectator") return events;
  const out: GameEvent[] = [];
  for (const e of events) {
    const who = eventAudience(state, e);
    if (who === null || who.includes(viewer)) out.push(e);
  }
  return out;
}

export function journalChronicle(
  events: readonly GameEvent[], state: GameState, content: ContentRegistry,
  viewer: PlayerId,
): readonly ChronicleLine[] {
  return chronicle(viewerNews(events, state, viewer), content, journalNames(state, content));
}
